import React, { useMemo, useEffect, useState, MouseEvent } from 'react';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  VisibilityStatusEnum,
  Expanded,
  Visible,
  createMenuItems,
  getVisibilityStatus,
  LayersData,
  layerElementId,
  switchAllVisible,
  checkAllVisible,
  LayerToolOptions,
} from './layerTool';
import { downloadDOM } from '../../../../../utils/download';
import { isMac } from '../../../../../utils/isMac';

export const Menu: React.FC<{
  layersData?: LayersData;
  setEnabled: (enabled: boolean) => void;
  visible: Visible;
  setVisible: (visible: Visible) => void;
  downloadAvailable: LayerToolOptions['downloadAvailable'];
  downloadUrlFn: LayerToolOptions['downloadUrlFn'];
  downloadGroupUrlFn: LayerToolOptions['downloadGroupUrlFn'];
  downloadOnClick: LayerToolOptions['downloadOnClick'];
}> = ({
  layersData,
  setEnabled,
  visible,
  setVisible,
  downloadAvailable,
  downloadUrlFn,
  downloadGroupUrlFn,
  downloadOnClick,
}) => {
  const [expanded, setExpanded] = useState<Expanded>({});
  useEffect(() => {
    setExpanded({});
  }, [layersData]);

  const items = useMemo(
    () => createMenuItems(layersData?.layers ?? [], expanded),
    [layersData, expanded],
  );

  const visibilityStatus = useMemo(
    () => getVisibilityStatus(layersData?.layers ?? [], visible),
    [layersData, visible],
  );

  const areAllVisible = useMemo(
    () => checkAllVisible(layersData?.layers ?? [], visible),
    [layersData, visible],
  );

  return (
    <div className="gen-layers-menu">
      <div className="gen-layers-menu-header">
        {items ? (
          <div
            className="gen-layers-menu-header__button"
            onClick={() => {
              const newVisible = switchAllVisible(layersData?.layers ?? [], !areAllVisible);
              setVisible(newVisible);
            }}
          >
            {areAllVisible ? 'Hide all' : 'Show all'}
          </div>
        ) : (
          <div />
        )}
        <div />
        <div className="gen-layers-menu-header__button" onClick={() => setEnabled(false)}>
          Close
        </div>
      </div>
      <div className="gen-layers-menu__items">
        <div className="gen-layers-items">
          {items.map((item) => {
            const { layer } = item;
            const expandable = layer.is_group && layer.nested_layers.length > 0;
            const expandPlus = expandable && !expanded[layer.id];
            const expandMinus = expandable && expanded[layer.id];
            return (
              <div key={layer.id} className="gen-layers-item">
                <div
                  className="gen-layers-item__visibility"
                  onClick={() => setVisible({ ...visible, [layer.id]: !visible[layer.id] })}
                >
                  {visibilityStatus[layer.id] === VisibilityStatusEnum.visible ? (
                    <EyeOutlined />
                  ) : visibilityStatus[layer.id] === VisibilityStatusEnum.parentInvisible ? (
                    <EyeInvisibleOutlined />
                  ) : (
                    <div className="gen-layers-item__invisible-icon" />
                  )}
                </div>
                <div className="gen-layers-item__indent">
                  {Array(item.indent)
                    .fill(null)
                    .map((v, indentIndex) => (
                      <div
                        key={indentIndex}
                        className={`gen-layers-item__indent-unit ${
                          indentIndex === item.indent - 1
                            ? item.isLast
                              ? 'gen-layers-item__indent-unit_angle'
                              : 'gen-layers-item__indent-unit_branch'
                            : 'gen-layers-item__indent-unit_line'
                        }`}
                      />
                    ))}
                </div>
                <div
                  className={`gen-layers-item__expand ${
                    expandPlus ? 'gen-layers-item__expand_plus' : ''
                  } ${expandMinus ? 'gen-layers-item__expand_minus' : ''} ${
                    !expandable ? 'gen-layers-item__expand_empty' : ''
                  } ${item.indent > 0 ? 'gen-layers-item__expand_indented' : ''}`}
                  onClick={
                    expandable
                      ? () => setExpanded({ ...expanded, [layer.id]: !expanded[layer.id] })
                      : undefined
                  }
                >
                  {expandPlus && <PlusSquareOutlined />}
                  {expandMinus && <MinusSquareOutlined />}
                </div>
                <div className="gen-layers-item__image-slot">
                  <svg
                    className="gen-layers-item__image"
                    viewBox={`0 0 ${layersData?.original_image.size[0] ?? 0} ${
                      layersData?.original_image.size[1] ?? 0
                    }`}
                  >
                    <use href={`#${layerElementId(layer.id)}`} />
                  </svg>
                  {downloadAvailable && (
                    <div
                      className="gen-layers-item__download"
                      title="Download"
                      onClick={(e: MouseEvent) => {
                        const isFullSize = isMac ? e.metaKey : e.altKey;
                        let url, fileName: string;
                        if (layer.is_group) {
                          fileName = '';
                          url = downloadGroupUrlFn(layer.id, isFullSize);
                        } else {
                          fileName = isFullSize ? layer.s3_key_fullsize : layer.s3_key_trimmed;
                          url = downloadUrlFn(fileName);
                        }
                        downloadDOM(url);
                        downloadOnClick?.({
                          layerName: layer.name,
                          fileName,
                          isGroup: layer.is_group,
                          isFullSize,
                          url,
                        });
                      }}
                    >
                      <DownloadOutlined />
                    </div>
                  )}
                </div>
                <div className="gen-layers-item__name">{layer.name}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
